/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @estee/elc-best-practice/uses-observer */
/***
 * The source of this file is from https://github.com/devfolioco/react-otp-input
 * Modification to meet our features includes:
 * 1. ios device auto fill:  autoComplete: 'one-time-code'
 * 2. clear focus once codes are inputted
 * 3. update for custom style support
 * 4. adjust functions order for eslint check
 * 5. paste with matched code numbers behavior
 ****/
import * as React from 'react';
type AllowedInputTypes = 'password' | 'text' | 'number' | 'tel';

type InputProps = Required<
    Pick<
        React.InputHTMLAttributes<HTMLInputElement>,
        | 'value'
        | 'onChange'
        | 'onFocus'
        | 'onBlur'
        | 'onKeyDown'
        | 'onPaste'
        | 'aria-label'
        | 'autoComplete'
        | 'style'
        | 'inputMode'
        | 'onInput'
    > & {
        ref: React.RefCallback<HTMLInputElement>;
        placeholder: string | undefined;
        className: string | undefined;
        type: AllowedInputTypes;
    }
>;

interface OTPInputProps {
    /** Value of the OTP inputs */
    value?: string;
    /** Number of OTP inputs to be rendered */
    numInputs?: number;
    /** Callback to be called when the OTP value changes */
    onChange(otp: string): void;
    /** Callback to be called when pasting content into the component */
    onPaste?(event: React.ClipboardEvent<HTMLDivElement>): void;
    /** Function to render the input */
    renderInput(inputProps: InputProps, index: number): React.ReactNode;
    /** Whether the first input should be auto focused */
    shouldAutoFocus?: boolean;
    /** Placeholder for the inputs */
    placeholder?: string;
    /** Function to render the separator */
    renderSeparator?: ((index: number) => React.ReactNode) | React.ReactNode;
    /** Style for the container */
    containerStyle?: React.CSSProperties | string;
    /** Style for the input */
    inputStyle?: React.CSSProperties | string;
    /** The type that will be passed to the input being rendered */
    inputType?: AllowedInputTypes;
}

const OTPInput = ({
    value = '',
    numInputs = 6,
    onChange,
    onPaste,
    renderInput,
    shouldAutoFocus = false,
    inputType = 'number',
    renderSeparator,
    placeholder,
    containerStyle,
    inputStyle
}: OTPInputProps) => {
    const [activeInput, setActiveInput] = React.useState(0);
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    const getOTPValue = () => (value ? value.toString().split('') : []);

    const isInputNum = inputType === 'number' || inputType === 'tel';

    React.useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, numInputs);
    }, [numInputs]);

    React.useEffect(() => {
        if (shouldAutoFocus) {
            inputRefs.current[0]?.focus();
        }
    }, [shouldAutoFocus]);

    const getPlaceholderValue = () => {
        if (typeof placeholder === 'string') {
            if (placeholder.length === numInputs) {
                return placeholder;
            }

            if (placeholder.length > 0) {
                console.error('Length of the placeholder should be equal to the number of inputs.');
            }
        }

        return undefined;
    };

    const isInputValueValid = (value: string) => {
        const isTypeValid = isInputNum ? !isNaN(Number(value)) : typeof value === 'string';

        return isTypeValid && value.trim().length === 1;
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => (index: number) => {
        setActiveInput(index);
        event.target.select();
    };

    const handleBlur = () => {
        setActiveInput(activeInput - 1);
    };

    const handleOTPChange = (otp: string[]) => {
        const otpValue = otp.join('');

        if (otpValue.length === numInputs) {
            inputRefs.current[activeInput]?.blur();
        }
        onChange(otpValue);
    };

    const changeCodeAtFocus = (value: string) => {
        const otp = getOTPValue();
        otp[activeInput] = value[0];
        handleOTPChange(otp);
    };

    const focusInput = (index: number) => {
        const activeInput = Math.max(Math.min(numInputs - 1, index), 0);

        if (inputRefs.current[activeInput]) {
            inputRefs.current[activeInput]?.focus();
            inputRefs.current[activeInput]?.select();
            setActiveInput(activeInput);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { nativeEvent } = event;
        const value = event.target.value;

        if (!isInputValueValid(value)) {
            // Pasting from the native autofill suggestion on a mobile device can pass
            // the pasted string as one long input to one of the cells. This ensures
            // that we handle the full input and not just the first character.
            if (value.length === numInputs) {
                const hasInvalidInput = value
                    .split('')
                    .some((cellInput) => !isInputValueValid(cellInput));
                if (!hasInvalidInput) {
                    handleOTPChange(value.split(''));
                    focusInput(numInputs - 1);
                }
            }

            // @ts-expect-error - This was added previously to handle and edge case
            // for dealing with keyCode "229 Unidentified" on Android. Check if this is
            // still needed.
            if (nativeEvent.data === null && nativeEvent.inputType === 'deleteContentBackward') {
                event.preventDefault();
                changeCodeAtFocus('');
                focusInput(activeInput - 1);
            }

            // Clear the input if it's not valid value because firefox allows
            // pasting non-numeric characters in a number type input
            event.target.value = '';
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        if (isInputValueValid(value)) {
            changeCodeAtFocus(value);
            if (activeInput < numInputs - 1) {
                focusInput(activeInput + 1);
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const otp = getOTPValue();
        if ([event.code, event.key].includes('Backspace')) {
            event.preventDefault();
            changeCodeAtFocus('');
            focusInput(activeInput - 1);
        } else if (event.code === 'Delete') {
            event.preventDefault();
            changeCodeAtFocus('');
        } else if (event.code === 'ArrowLeft') {
            event.preventDefault();
            focusInput(activeInput - 1);
        } else if (event.code === 'ArrowRight') {
            event.preventDefault();
            focusInput(activeInput + 1);
        } else if (event.key === otp[activeInput]) {
            event.preventDefault();
            focusInput(activeInput + 1);
        } else if (
            event.code === 'Spacebar' ||
            event.code === 'Space' ||
            event.code === 'ArrowUp' ||
            event.code === 'ArrowDown'
        ) {
            event.preventDefault();
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        let startInput = 0;

        if (event.clipboardData.getData('text/plain').length !== numInputs) {
            startInput = activeInput;
        }

        const otp = getOTPValue();
        let nextActiveInput = startInput;

        // Get pastedData in an array of max size (num of inputs - current position)
        const pastedData = event.clipboardData
            .getData('text/plain')
            .slice(0, numInputs - startInput)
            .split('');

        // Prevent pasting if the clipboard data contains non-numeric values for number inputs
        if (isInputNum && pastedData.some((value) => isNaN(Number(value)))) {
            return;
        }

        // Paste data from focused input onwards
        for (let pos = 0; pos < numInputs; ++pos) {
            if (pos >= startInput && pastedData.length > 0) {
                otp[pos] = pastedData.shift() ?? '';
                nextActiveInput++;
            }
        }

        if (nextActiveInput < numInputs - 1) {
            focusInput(nextActiveInput);
        }
        handleOTPChange(otp);
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center'
            }}
            className={typeof containerStyle === 'string' ? containerStyle : undefined}
            onPaste={onPaste}
        >
            {Array.from({ length: numInputs }, (_, index) => index).map((index) => (
                <React.Fragment key={index}>
                    {renderInput(
                        {
                            value: getOTPValue()[index] ?? '',
                            placeholder: getPlaceholderValue()?.[index] ?? undefined,
                            ref: (element) => (inputRefs.current[index] = element),
                            onChange: handleChange,
                            onFocus: (event) => handleFocus(event)(index),
                            onBlur: handleBlur,
                            onKeyDown: handleKeyDown,
                            onPaste: handlePaste,
                            autoComplete: 'one-time-code',
                            'aria-label': `Please enter OTP character ${index + 1}`,
                            style: {
                                ...(typeof inputStyle === 'object' && inputStyle !== null
                                    ? inputStyle
                                    : {})
                            },
                            className: typeof inputStyle === 'string' ? inputStyle : undefined,
                            type: inputType,
                            inputMode: isInputNum ? 'numeric' : 'text',
                            onInput: handleInputChange
                        },
                        index
                    )}
                    {index < numInputs - 1 &&
                        (typeof renderSeparator === 'function'
                            ? renderSeparator(index)
                            : renderSeparator)}
                </React.Fragment>
            ))}
        </div>
    );
};

export type { OTPInputProps, InputProps, AllowedInputTypes };
export default OTPInput;
